import { type ReactNode, useEffect, useState } from "react"
import { motion } from "motion/react"
import { appStore } from "../../store/appStore"
import { useRequest } from "../../hooks/useRequest"
import { mapCinemaContactApi } from "../../lib/cinemaContacts"
import { openSupportChat, refreshSupportUser } from "../../lib/chatwoot"
import type { CinemaContactApi, CinemaContactDetails, ContactDepartment } from "../../data/cinemaContacts.types"
import type { Cinema } from "../../types/entities/cinema"
import type { City } from "../../types/entities/city"
import type { User } from "../../types/entities/user"
import OsmMap from "../../features/contacts/OsmMap"
import PageTitle from "../../components/PageTitle"
import { userStore } from "../../store/userStore"

const DISPLAY = "'Tilda Sans', sans-serif"
const BODY = "'Manrope', sans-serif"

function phoneHref(phone: string): string {
    return `tel:${phone.replace(/[^\d+]/g, "")}`
}

function InfoRow({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className="flex flex-col gap-1 sm:flex-row sm:gap-4">
            <dt
                className="shrink-0 text-[12px] uppercase tracking-[0.06em] text-white/40 sm:w-44"
                style={{ fontFamily: BODY, fontWeight: 700 }}>
                {label}
            </dt>
            <dd className="text-[15px] leading-relaxed text-white/85" style={{ fontFamily: BODY, fontWeight: 500 }}>
                {children}
            </dd>
        </div>
    )
}

function SectionCard({ title, children }: { title: string; children: ReactNode }) {
    return (
        <section className="rounded-[24px] border border-white/10 bg-white/[0.04] p-4 md:p-5">
            <h2
                className="mb-4 text-white"
                style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 18, letterSpacing: "-0.01em" }}>
                {title}
            </h2>
            {children}
        </section>
    )
}

function DepartmentContent({ dept }: { dept: ContactDepartment }) {
    return (
        <div className="flex flex-col gap-1" style={{ fontFamily: BODY, fontWeight: 500 }}>
            {dept.contactPerson && <p className="text-white/85">{dept.contactPerson}</p>}
            {dept.phones?.map(phone => (
                <p key={phone}>
                    <a href={phoneHref(phone)} className="text-white/85 transition-colors hover:text-white" style={{ fontWeight: 600 }}>
                        {phone}
                    </a>
                </p>
            ))}
            {dept.emails?.map(email => (
                <p key={email}>
                    <a href={`mailto:${email}`} className="text-[#349be6] underline underline-offset-2 transition-colors hover:text-[#5eb3f0]">
                        {email}
                    </a>
                </p>
            ))}
        </div>
    )
}

function DepartmentCard({ dept }: { dept: ContactDepartment }) {
    return (
        <div className="rounded-[18px] border border-white/[0.08] bg-white/[0.03] p-4 flex flex-col gap-2">
            <h3 className="text-white text-[14px]" style={{ fontFamily: DISPLAY, fontWeight: 700 }}>
                {dept.title}
            </h3>
            <DepartmentContent dept={dept} />
        </div>
    )
}

function normalizeExternalUrl(raw?: string | null): string | null {
    if (!raw) return null
    let url = raw.trim()
    if (!url) return null
    // в сидах иногда лежит urlencode(...)
    try {
        if (/%2F|%3A/i.test(url) && !/^https?:\/\//i.test(url)) {
            url = decodeURIComponent(url)
        }
    } catch {
        /* keep raw */
    }
    if (!/^https?:\/\//i.test(url)) return null
    return url
}

function ReviewCta({ href }: { href: string }) {
    return (
        <motion.a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12 }}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            className="group relative flex items-center justify-between gap-4 overflow-hidden rounded-[24px] border border-[#e4206b]/35 px-5 py-5 md:px-7 md:py-6"
            style={{
                background:
                    "linear-gradient(135deg, rgba(228,32,107,0.28) 0%, rgba(52,155,230,0.16) 55%, rgba(255,255,255,0.04) 100%)",
            }}>
            <div
                className="pointer-events-none absolute -right-8 -top-10 h-36 w-36 rounded-full opacity-40 blur-2xl transition-opacity group-hover:opacity-70"
                style={{ background: "#e4206b" }}
            />
            <div className="relative z-[1] flex min-w-0 flex-col gap-1.5">
                <p
                    className="text-[11px] uppercase tracking-[0.12em] text-white/50"
                    style={{ fontFamily: BODY, fontWeight: 700 }}>
                    Ваше мнение важно
                </p>
                <p
                    className="text-[20px] leading-tight text-white md:text-[24px]"
                    style={{ fontFamily: DISPLAY, fontWeight: 700, letterSpacing: "-0.02em" }}>
                    Оставить отзыв
                </p>
                <p className="text-[13px] text-white/60 md:text-[14px]" style={{ fontFamily: BODY, fontWeight: 500 }}>
                    Короткий отзыв — быстрее трейлера
                </p>
            </div>
            <span
                className="relative z-[1] flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#e4206b] text-white shadow-[0_8px_24px_rgba(228,32,107,0.45)] transition-transform group-hover:translate-x-0.5"
                aria-hidden>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </span>
        </motion.a>
    )
}

function SupportCta({ onClick, loading }: { onClick: () => void; loading: boolean }) {
    return (
        <motion.button
            type="button"
            onClick={onClick}
            disabled={loading}
            aria-busy={loading}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.16 }}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            className="group relative flex cursor-pointer items-center justify-between gap-4 overflow-hidden rounded-[24px] border border-[#349be6]/35 px-5 py-5 text-left disabled:cursor-wait disabled:opacity-60 md:px-7 md:py-6"
            style={{
                background:
                    "linear-gradient(135deg, rgba(52,155,230,0.26) 0%, rgba(228,32,107,0.14) 58%, rgba(255,255,255,0.04) 100%)",
            }}>
            <div
                className="pointer-events-none absolute -right-8 -top-10 h-36 w-36 rounded-full opacity-35 blur-2xl transition-opacity group-hover:opacity-65"
                style={{ background: "#349be6" }}
            />
            <div className="relative z-[1] flex min-w-0 flex-col gap-1.5">
                <p
                    className="text-[11px] uppercase tracking-[0.12em] text-white/50"
                    style={{ fontFamily: BODY, fontWeight: 700 }}>
                    Нужна помощь
                </p>
                <p
                    className="text-[20px] leading-tight text-white md:text-[24px]"
                    style={{ fontFamily: DISPLAY, fontWeight: 700, letterSpacing: "-0.02em" }}>
                    {loading ? "Открываем чат..." : "Написать в поддержку"}
                </p>
                <p className="text-[13px] text-white/60 md:text-[14px]" style={{ fontFamily: BODY, fontWeight: 500 }}>
                    Поможем с билетами, оплатой и входом
                </p>
            </div>
            <span
                className="relative z-[1] flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#349be6] text-white shadow-[0_8px_24px_rgba(52,155,230,0.38)] transition-transform group-hover:translate-x-0.5"
                aria-hidden>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M5 12h9M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </span>
        </motion.button>
    )
}

function fallbackFromCinema(cinema: Cinema, city: City, locationSelected: boolean): CinemaContactDetails {
    return {
        cinemaId: cinema.id,
        seoName: locationSelected ? `${cinema.full_name ?? cinema.name}, ${city.name}` : "Мягкий кинотеатр",
        sessionsPhone: cinema.phone || null,
        email: cinema.contact_email || null,
        schedule:
            cinema.hour?.start && cinema.hour?.end
                ? `ПН–ВС: ${cinema.hour.start} – ${cinema.hour.end}${cinema.hour.next_day ? " (след. день)" : ""}`
                : null,
        address: cinema.address || null,
        latitude: cinema.geo?.latitude != null ? String(cinema.geo.latitude) : null,
        longitude: cinema.geo?.longitude != null ? String(cinema.geo.longitude) : null,
        workTimes: [],
        social: { vk: null, max: null },
        linkToComment: cinema.link_to_comment || null,
        departments: [],
        requisites: null,
    }
}

export default function Contacts() {
    const { cinema, city, locationSelected, setError } = appStore()
    const { request, requestData } = useRequest()
    const { user, setUser, deviceToken } = userStore()
    const [details, setDetails] = useState<CinemaContactDetails | null>(null)
    const [loading, setLoading] = useState(true)
    const [fromApi, setFromApi] = useState(false)
    const [supportOpening, setSupportOpening] = useState(false)

    useEffect(() => {
        let cancelled = false
        if (!cinema?.id) {
            setDetails(null)
            setLoading(false)
            return
        }

        const load = async () => {
            setLoading(true)
            const res = await requestData<CinemaContactApi>({
                type: "GET",
                path: `cinema/${cinema.id}/contacts?channel=site`,
                disableError: true,
            })
            if (cancelled) return

            if (res.status === "success" && res.data && typeof res.data === "object") {
                const mapped = mapCinemaContactApi(res.data as CinemaContactApi)
                if (mapped) {
                    setDetails(mapped)
                    setFromApi(true)
                    setLoading(false)
                    return
                }
            }

            setDetails(fallbackFromCinema(cinema, city, locationSelected))
            setFromApi(false)
            setLoading(false)
        }

        load()
        return () => {
            cancelled = true
        }
        // requestData намеренно не в deps
    }, [cinema?.id, city?.id, locationSelected])

    const lat = Number(details?.latitude ?? cinema.geo?.latitude)
    const lng = Number(details?.longitude ?? cinema.geo?.longitude)
    const hasMap = Number.isFinite(lat) && Number.isFinite(lng)

    const cinemaTitle =
        details?.seoName ||
        (locationSelected ? `${cinema.full_name ?? cinema.name}, ${city.name}` : "Мягкий кинотеатр")

    const sessionsPhone = details?.sessionsPhone ?? cinema.phone
    const schedule =
        details?.schedule ??
        (cinema.hour?.start && cinema.hour?.end
            ? `ПН–ВС: ${cinema.hour.start} – ${cinema.hour.end}${cinema.hour.next_day ? " (след. день)" : ""}`
            : null)
    const address = details?.address ?? cinema.address
    const email = details?.email || cinema.contact_email
    const social = details?.social
    const departments = details?.departments ?? []
    const requisites = details?.requisites
    const reviewUrl = normalizeExternalUrl(details?.linkToComment ?? cinema.link_to_comment)

    const handleSupportClick = () => {
        if (supportOpening) return

        const open = async () => {
            setSupportOpening(true)
            const currentUser = await refreshSupportUser(user, () =>
                request<User>("GET", `user?city_id=${city.id}`, undefined, { disableError: true })
            )
            if (currentUser) setUser(currentUser)

            await openSupportChat({
                user: currentUser,
                deviceToken,
                cityId: city.id,
                cityName: city.name,
                cinemaName: cinema.name,
            })
        }

        open()
            .catch(() => setError("Не удалось открыть чат поддержки. Попробуйте ещё раз."))
            .finally(() => setSupportOpening(false))
    }

    if (loading) {
        return (
            <div className="flex flex-col gap-6 px-4 pt-16 pb-6 md:px-6 lg:px-8">
                <PageTitle>Контакты</PageTitle>
                <div className="h-40 animate-pulse rounded-[24px] bg-white/[0.06]" />
                <div className="h-64 animate-pulse rounded-[24px] bg-white/[0.06]" />
            </div>
        )
    }

    const mapBlock = (embedded = false) =>
        hasMap ? (
            <OsmMap
                latitude={lat}
                longitude={lng}
                title={address ?? "Кинотеатр на карте"}
                fillHeight={!embedded}
                embedded={embedded}
            />
        ) : null

    const contactsList = (
        <dl className="flex flex-col gap-4">
            {sessionsPhone && (
                <InfoRow label="Информация о сеансах">
                    <a href={phoneHref(sessionsPhone)} className="transition-colors hover:text-white" style={{ fontWeight: 600 }}>
                        {sessionsPhone}
                    </a>
                </InfoRow>
            )}
            {schedule && (
                <InfoRow label="График работы">
                    <span className="whitespace-pre-line" style={{ fontWeight: 600 }}>
                        {schedule}
                    </span>
                </InfoRow>
            )}
            {address && <InfoRow label="Адрес">{address}</InfoRow>}
            {email && (
                <InfoRow label="E-mail">
                    <a href={`mailto:${email}`} className="text-[#349be6] underline underline-offset-2 transition-colors hover:text-[#5eb3f0]">
                        {email}
                    </a>
                </InfoRow>
            )}
            {(social?.vk || social?.max) && (
                <InfoRow label="Соцсети">
                    <div className="flex flex-col gap-1">
                        {social.vk && (
                            <a href={social.vk} target="_blank" rel="noreferrer" className="text-[#349be6] underline underline-offset-2">
                                ВКонтакте
                            </a>
                        )}
                        {social.max && (
                            <a href={social.max} target="_blank" rel="noreferrer" className="text-[#349be6] underline underline-offset-2">
                                Max
                            </a>
                        )}
                    </div>
                </InfoRow>
            )}
            {departments.map((dept, index) => (
                <InfoRow key={`${dept.title}-${index}`} label={dept.title}>
                    <DepartmentContent dept={dept} />
                </InfoRow>
            ))}
        </dl>
    )

    return (
        <div className="flex flex-col gap-6 px-4 pt-16 pb-6 md:px-6 lg:px-8">
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col gap-2 pt-2 pb-3">
                <PageTitle>Контакты</PageTitle>
                <p className="text-[14px] text-white/45 lg:hidden" style={{ fontFamily: BODY, fontWeight: 500 }}>
                    {cinemaTitle}
                </p>
            </motion.div>

            {hasMap && (
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 }}
                    className="lg:hidden">
                    {mapBlock()}
                </motion.div>
            )}

            <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="hidden lg:flex lg:overflow-hidden lg:rounded-[24px] lg:border lg:border-white/10">
                <section className="min-w-0 flex-[0.95] bg-white/[0.04] p-4 md:p-5">
                    <h2
                        className="mb-4 text-white"
                        style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 18, letterSpacing: "-0.01em" }}>
                        Контакты
                    </h2>
                    {contactsList}
                </section>
                {hasMap && (
                    <div className="flex min-w-0 flex-[1.05] flex-col self-stretch border-l border-white/10">
                        {mapBlock(true)}
                    </div>
                )}
            </motion.div>

            <div className={reviewUrl ? "grid gap-4 md:grid-cols-2" : ""}>
                {reviewUrl && <ReviewCta href={reviewUrl} />}
                <SupportCta onClick={handleSupportClick} loading={supportOpening} />
            </div>

            <div className="flex flex-col gap-6 lg:hidden">
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                    <SectionCard title="Информация о сеансах">
                        <dl className="flex flex-col gap-4">
                            {sessionsPhone && (
                                <InfoRow label="Телефон">
                                    <a href={phoneHref(sessionsPhone)} className="transition-colors hover:text-white" style={{ fontWeight: 600 }}>
                                        {sessionsPhone}
                                    </a>
                                </InfoRow>
                            )}
                            {schedule && (
                                <InfoRow label="График работы">
                                    <span className="whitespace-pre-line" style={{ fontWeight: 600 }}>
                                        {schedule}
                                    </span>
                                </InfoRow>
                            )}
                            {address && <InfoRow label="Адрес">{address}</InfoRow>}
                            {email && (
                                <InfoRow label="E-mail">
                                    <a href={`mailto:${email}`} className="transition-colors hover:text-white">
                                        {email}
                                    </a>
                                </InfoRow>
                            )}
                        </dl>
                    </SectionCard>
                </motion.div>

                {departments.length > 0 && (
                    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
                        <SectionCard title="Отделы">
                            <div className="flex flex-col gap-4">
                                {departments.map((dept, index) => (
                                    <DepartmentCard key={`${dept.title}-${index}`} dept={dept} />
                                ))}
                            </div>
                        </SectionCard>
                    </motion.div>
                )}
            </div>

            {requisites && (
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                    <SectionCard title="Реквизиты">
                        <dl className="flex flex-col gap-4">
                            <InfoRow label="Название">{requisites.name}</InfoRow>
                            <InfoRow label="ИНН">{requisites.inn}</InfoRow>
                            <InfoRow label="КПП">{requisites.kpp}</InfoRow>
                            <InfoRow label="ОГРН">{requisites.ogrn}</InfoRow>
                            <InfoRow label="Фактический адрес">{requisites.actualAddress}</InfoRow>
                            <InfoRow label="Юридический адрес">{requisites.legalAddress}</InfoRow>
                            <InfoRow label="р/с">{requisites.bankAccount}</InfoRow>
                            <InfoRow label="к/с">{requisites.corrAccount}</InfoRow>
                            <InfoRow label="БИК">{requisites.bik}</InfoRow>
                        </dl>
                    </SectionCard>
                </motion.div>
            )}

            {!fromApi && (
                <p className="pb-4 text-center text-[12px] text-white/30" style={{ fontFamily: BODY }}>
                    Подробные контакты и реквизиты для этого кинотеатра скоро появятся
                </p>
            )}
        </div>
    )
}
